/**
 * "What are you actually looking for?" asked as a ranked ladder of eight
 * things a job search trades between, plus a pile for the ones that do not
 * matter to this applicant.
 *
 * Every rule and every sentence lives here rather than in the component, for
 * the same reason as the level ladder: the copy is the feature, and an answer
 * summarised wrongly is a content bug no render test would catch.
 *
 * Order is the meaning. `ranked` is stored most-important-first and the top
 * three are the ones the follow-up questions will be about, so nothing here
 * may sort, dedupe by set, or otherwise treat the array as unordered.
 */
import { z } from 'zod'
import type { FollowUpAnswers } from './priority-followups.js'
import {
  asksFollowUps,
  emptyFollowUps,
  followUpLine,
  hasFollowUpAnswers,
  isFollowUpAnswers,
  knownFollowUps,
  settleFollowUps,
} from './priority-followups.js'
import type { WidgetDescriptor } from './widgets.js'

interface PriorityItem {
  id: string
  /** The applicant's own words for it, and what the admin recap prints. */
  label: string
}

/**
 * The eight, in the order we hand them over. This starting order is OURS —
 * see `startingPriorities` for why that matters and what stops it being
 * mistaken for an answer.
 */
export const PRIORITY_ITEMS = [
  { id: 'comp', label: 'More compensation' },
  { id: 'brand', label: 'A prestigious company' },
  { id: 'balance', label: 'Work-life balance / great company culture' },
  { id: 'advance', label: 'Career advancement / growth' },
  { id: 'meaning', label: 'More meaningful work' },
  { id: 'stability', label: 'Long-term job stability' },
  { id: 'switch', label: 'Lets me pursue a different specialty' },
  { id: 'flex', label: 'Work flexibility, remote or hybrid' },
] as const satisfies readonly PriorityItem[]

/**
 * The eight ids as a type, so the module's own constants have to name real
 * items. Deliberately NOT the type of a stored `ranked`/`dropped` entry —
 * those stay `string`, because a row written before we retired an item is
 * still that applicant's answer and still has to be readable.
 */
export type PriorityId = (typeof PRIORITY_ITEMS)[number]['id']

export const PRIORITY_IDS: readonly PriorityId[] = PRIORITY_ITEMS.map(
  (i) => i.id,
)

/**
 * The module's vocabulary fence: whether a stored string still names one of
 * the eight. This is the seam where a `string` out of the jsonb bag meets the
 * `PriorityId` union, so it is spelled as a predicate rather than an
 * assertion — the same reason the timeline's stored-span guard carries no
 * unverified claims. The write path reuses it through `settlePriorities`.
 */
export function isPriorityId(id: string): id is PriorityId {
  return PRIORITY_IDS.some((known) => known === id)
}

/**
 * The sentences the component draws around the cards.
 *
 * Here rather than in the component for the reason in the header: the copy is
 * the feature, and the shipped sibling widget keeps every user-visible word in
 * its rules module too — its component holds exactly one string, a structural
 * aria-label. A heading living in the component is also how `prioritize` and
 * `prioritising` ended up naming the same pile two different ways.
 */
export const PRIORITY_COPY = {
  /** Names the dropped pile. The applicant's claim, in their tense. */
  pileHeading: 'This job search will not prioritize',
  pileHint: 'Tap one to add it back',
  /** Shown in place of the ladder when every card has been dropped. */
  emptyLadder: 'Nothing on your list. Add anything below that does matter.',
  /** The heading over phase two. Phase one is the question's own label. */
  detailHeading: 'A little more on your top priorities',
  /**
   * Why the order has to be settled first. Said before they start, because
   * discovering afterwards that the ranking chose the questions is what makes
   * somebody feel a form has wasted their time.
   */
  rankHint:
    'What comes next asks about whatever ends up on top, so settle this order first.',
  /**
   * The two controls that move between the phases.
   *
   * Neither says "Next" or "Back", and that is not a style preference: the
   * wizard's own two nav buttons at the foot of every step are called exactly
   * that, and a page offering two Nexts makes the applicant guess which one
   * leaves the question. These say where they go instead.
   */
  toDetail: 'A little more on these',
  toRank: 'Change the ranking',
  /** Shown in phase two when nothing on top has a pile to ask. */
  detailEmpty:
    'Nothing on your list has anything more to ask about. Change the ranking and add whatever does matter.',
  /** Beside a group offering a free-text chip, once that chip is picked. */
  otherLabel: 'Tell us which',
  /** Beside the box on the one pile that can be extended by searching. */
  searchLabel: 'Search',
} as const

/**
 * How many of the ranked cards carry the weight of the answer. These are the
 * three drawn differently, the three the readback names, and the only three
 * the follow-up step will ask about.
 */
export const TOP_COUNT = 3

/**
 * The two that start off the ladder. Handing somebody eight ranked cards
 * invites them to accept the order we chose; two sitting in the dropped pile
 * makes the list visibly theirs to fix, and adding one back is a louder
 * answer than leaving it where it was.
 */
const OFF_LADDER_AT_START: readonly PriorityId[] = ['switch', 'flex']

/**
 * The stored answer: two ordered lists of item ids, in the `answers` jsonb
 * bag under `jobPriorities`. Ids rather than labels so rewording a priority
 * never rewrites an answer somebody already gave, and never a column — see
 * the ticket's reversibility constraints.
 */
export interface PriorityValue {
  /** Most important first. Position IS the ranking. */
  ranked: string[]
  /** "This job search will not prioritize …", in the order they were dropped. */
  dropped: string[]
  /**
   * What they said under the three that ended on top — phase two of the same
   * question, and so part of the same answer.
   *
   * Here rather than in a field of its own because the follow-ups have no
   * meaning apart from the ranking that decided which ones to ask: split
   * across two keys, a row could hold office-day answers with no flexibility
   * card anywhere near the top and nothing would say which was stale. The
   * ladder's own tolerant guard was written to expect this key arriving one
   * day, which is why adding it does not blank a single older row.
   *
   * Optional, and absent rather than empty when nothing has been said: an
   * untouched question must not leave scaffolding in the answers bag.
   */
  followUps?: FollowUpAnswers
}

/** What the widget draws, which is not always exactly what is stored. */
export interface PriorityBoard {
  ranked: string[]
  dropped: string[]
  /** The first `TOP_COUNT` ranked cards, or fewer if the ladder is short. */
  top: string[]
}

/** A priority as display text; an unknown id passes straight through. */
export function priorityLabel(id: string): string {
  return PRIORITY_ITEMS.find((i) => i.id === id)?.label ?? id
}

/** The same, lowercased to sit mid-sentence in the readback. */
function priorityPhrase(id: string): string {
  const label = priorityLabel(id)
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * The board we hand over before they touch anything.
 *
 * This is deliberately NOT an answer — `isPrioritiesAnswered` returns false
 * for it, and the wizard stores nothing until the applicant moves a card. A
 * ranking they were handed and a ranking they gave must never be the same
 * row in the admin recap.
 */
export function startingPriorities(): PriorityValue {
  return {
    ranked: PRIORITY_IDS.filter((id) => !OFF_LADDER_AT_START.includes(id)),
    dropped: [...OFF_LADDER_AT_START],
  }
}

/** Whether a value is the stored shape, for the tolerant admin read path. */
export function isPriorityValue(value: unknown): value is PriorityValue {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false
  // The two piles have to be there; anything else in the object is allowed
  // through. Strictness belongs to the write path, where
  // `storedPrioritiesSchema` already refuses an unknown key. Demanding
  // exactly two keys here would make the day the follow-up step stores
  // something alongside the ranking the day every older row renders as blank
  // in the admin recap, indistinguishable from never having been answered.
  const { ranked, dropped } = value as Record<string, unknown>
  return [ranked, dropped].every(
    (list) => Array.isArray(list) && list.every((x) => typeof x === 'string'),
  )
}

/**
 * A stored value put in order: junk rejected, each id kept once, and the
 * ladder winning when an id somehow appears in both piles.
 *
 * The TOLERANT half of the pair. A retired id survives this on purpose — a
 * row written before we dropped a priority is still that applicant's answer,
 * `priorityLabel` is built to print an id it does not recognise, and the
 * ticket asks that an answer to a question we later removed still renders.
 * Filtering here would have deleted it before `priorityLabel` ever saw it.
 * `knownPriorities` is the half that restricts to today's eight.
 *
 * What it deliberately does NOT do is add missing ids. Adding a ninth
 * priority later must not retroactively file it at the bottom of somebody's
 * ranking ("they ranked it last") or into their dropped pile ("they said it
 * does not matter") — both are claims the applicant never made. The stored
 * answer holds only placements that survived an edit; `priorityBoard` is what
 * puts an unplaced card somewhere reachable for editing.
 */
export function settlePriorities(value: unknown): PriorityValue {
  if (!isPriorityValue(value)) return { ranked: [], dropped: [] }
  const seen = new Set<string>()
  const keep = (list: string[]) =>
    list.filter((id) => {
      if (seen.has(id)) return false
      seen.add(id)
      return true
    })
  // Ranked first, so it is the pile that wins a duplicate across both.
  const ranked = keep(value.ranked)
  const settled: PriorityValue = { ranked, dropped: keep(value.dropped) }
  // Settled by its own module, and only attached when it says something. A
  // `followUps: { picks: {}, typed: {} }` sitting on every row would make an
  // untouched question indistinguishable from one somebody opened and left.
  const followUps = settleFollowUps(value.followUps)
  if (hasFollowUpAnswers(followUps)) settled.followUps = followUps
  return settled
}

/**
 * Settled, then cut down to the eight we ship today.
 *
 * The STRICT half. Three callers, none of which can deal in an id that no
 * longer exists: `priorityBoard`, because a card with no label is a card
 * nobody can read; the wire schema, because the server is where an id we
 * never shipped gets refused; and the descriptor's `settle`, because a
 * settled answer has to be the answer the board DRAWS, and the board draws
 * this. Everything that merely READS an answer — the recap, the readback,
 * answeredness — uses `settlePriorities` instead and keeps the retired id.
 */
export function knownPriorities(value: unknown): PriorityValue {
  const settled = settlePriorities(value)
  const known: PriorityValue = {
    ranked: settled.ranked.filter(isPriorityId),
    dropped: settled.dropped.filter(isPriorityId),
  }
  const followUps = knownFollowUps(settled.followUps)
  if (hasFollowUpAnswers(followUps)) known.followUps = followUps
  return known
}

/**
 * What to draw. Every known item appears exactly once, with anything the
 * stored answer never placed appended to the bottom of the ladder — a card
 * nobody can reach is a card nobody can answer, and the bottom is the
 * quietest place to put one.
 */
export function priorityBoard(value: PriorityValue): PriorityBoard {
  const settled = knownPriorities(value)
  // Nothing placed at all is a question nobody has reached yet, not a ladder
  // somebody emptied — emptying it puts all eight on the dropped pile. This
  // is the single place that turns "no answer" into something drawable, so
  // the stored answer can stay honestly empty until they touch it.
  const base =
    settled.ranked.length === 0 && settled.dropped.length === 0
      ? startingPriorities()
      : settled
  const placed = new Set([...base.ranked, ...base.dropped])
  const ranked = [
    ...base.ranked,
    ...PRIORITY_IDS.filter((id) => !placed.has(id)),
  ]
  return { ranked, dropped: base.dropped, top: ranked.slice(0, TOP_COUNT) }
}

/**
 * Whether this is a ranking the applicant gave rather than the one we handed
 * them.
 *
 * Nothing writes a board the applicant did not produce: `emptyValue`, the
 * optional transform in `stepSchema` and `settleWidgets` all yield two empty
 * piles, and the widget calls `onChange` only from a real edit. So "anything
 * is placed" IS "they touched it".
 *
 * Which is not the same as "the stored value is never our starting order" —
 * tap a card down and back up and it lands there again. That row has been
 * answered, and printing it is right: they looked and left it. Nothing here
 * depends on telling the two apart, which is the point.
 *
 * Deliberately not a comparison against `startingPriorities()`: that would
 * make today's item list a permanent input to the meaning of every row
 * already stored. Reorder the eight, or add a ninth, and rows that matched
 * the old start would silently begin reading as answers while rows matching
 * the new one stopped — the same retroactive claim `settlePriorities` refuses
 * to make. It also left an applicant who genuinely agrees with our order
 * unable to say so.
 */
export function isPrioritiesAnswered(value: PriorityValue): boolean {
  const settled = settlePriorities(value)
  if (settled.ranked.length > 0 || settled.dropped.length > 0) return true
  // Answering phase two without moving a card still counts. Phase two names
  // the three it is asking about and says in as many words that changing the
  // ranking changes them, so somebody who answered those three read that
  // order and let it stand — the same "they looked and left it" the paragraph
  // above is about, reached by a different route.
  return hasFollowUpAnswers(settleFollowUps(settled.followUps))
}

/**
 * Move a ranked card one place up (-1) or down (+1).
 *
 * Returns the value it was given, by identity, when the move is impossible —
 * the caller uses that to decide whether anything is worth saving, so a
 * refused tap must not look like an edit.
 */
export function movePriority(
  value: PriorityValue,
  id: string,
  delta: -1 | 1,
): PriorityValue {
  const from = value.ranked.indexOf(id)
  const to = from + delta
  if (from === -1 || to < 0 || to >= value.ranked.length) return value
  const ranked = [...value.ranked]
  ranked.splice(to, 0, ...ranked.splice(from, 1))
  return { ...value, ranked }
}

/** Take a card off the ladder. A card already off it is left alone. */
export function dropPriority(value: PriorityValue, id: string): PriorityValue {
  if (!value.ranked.includes(id)) return value
  // Spread first, like `movePriority`: reordering the ladder must not throw
  // away what was said under the top three. A card demoted out of the top
  // three keeps its follow-up answers, so promoting it back is an undo rather
  // than a second round of the same questions.
  return {
    ...value,
    ranked: value.ranked.filter((x) => x !== id),
    dropped: [...value.dropped, id],
  }
}

/**
 * Put a dropped card back on the ladder.
 *
 * `at` is the rank it was taken from, when the caller still remembers it —
 * an undo, in other words, and then putting it back there is restoring the
 * applicant's own claim rather than inventing one. Without it the card lands
 * at the bottom: they had said it did not matter, and the bottom is the only
 * thing they actually told us.
 *
 * The distinction is what makes a mis-tap survivable. The remove control sits
 * at the end of a row, and after a removal the next card slides up so its own
 * remove control is under the same finger — so a stray second tap on a phone
 * takes a card nobody meant to take. There is no way to tell that apart from
 * a deliberate quick clear (both are the same coordinates a moment apart), so
 * the answer is not to guess at intent but to make being wrong cost one tap
 * instead of a rebuilt ranking.
 *
 * Clamped, because the ladder may have got shorter since.
 */
export function restorePriority(
  value: PriorityValue,
  id: string,
  at?: number,
): PriorityValue {
  if (!value.dropped.includes(id)) return value
  const ranked = [...value.ranked]
  const where =
    at === undefined ? ranked.length : Math.min(Math.max(at, 0), ranked.length)
  ranked.splice(where, 0, id)
  return { ...value, ranked, dropped: value.dropped.filter((x) => x !== id) }
}

/**
 * The answer as one line for the admin recap: the ranking, numbered, then
 * what they said they will not chase. The second half is not a footnote —
 * "not prioritizing compensation" is often the more useful sentence.
 */
export function prioritiesSummary(value: PriorityValue): string {
  // The gate lives here rather than in each caller. An untouched question
  // stores nothing while the widget draws a full board of starting cards, so
  // a caller that reached for the drawn board instead of the stored value
  // would print our defaults as the applicant's words.
  if (!isPrioritiesAnswered(value)) return ''
  const { ranked, dropped } = settlePriorities(value)
  const parts: string[] = []
  if (ranked.length > 0)
    parts.push(
      ranked.map((id, i) => `${i + 1}. ${priorityLabel(id)}`).join(' · '),
    )
  if (dropped.length > 0)
    parts.push(`Not prioritizing: ${dropped.map(priorityLabel).join(', ')}`)
  return parts.join(' — ')
}

/**
 * What they said under the top three, one line per priority that said
 * anything, for the admin read path.
 *
 * Read off the DRAWN board's top three rather than the stored ranking: those
 * are the three phase two actually asked about, including when the ranking
 * they left standing is the one we handed them. A priority that has since
 * fallen out of the top three keeps its answers in storage — that is what
 * makes re-ranking survivable — but it is not asked and so is not printed.
 */
export function followUpSummary(value: PriorityValue): string[] {
  if (!isPrioritiesAnswered(value)) return []
  const answers = settleFollowUps(settlePriorities(value).followUps)
  const lines: string[] = []
  for (const id of priorityBoard(value).top) {
    const line = followUpLine(id, answers)
    if (line !== '') lines.push(`${priorityLabel(id)}: ${line}`)
  }
  return lines
}

/**
 * The plain-English readback under the widget. A ranking without one is a
 * puzzle: the cards say what the options are, this says what the applicant
 * has actually claimed.
 */
export function prioritiesReadback(value: PriorityValue): string {
  const { ranked } = priorityBoard(value)
  if (ranked.length === 0)
    return 'You have taken everything off the list. Add back whatever does matter.'
  if (!isPrioritiesAnswered(value))
    return 'This is our order, not yours. Move what matters to the top.'
  // Named from the STORED ranking, never the drawn board. `priorityBoard`
  // appends cards the answer never placed so they stay reachable, and a
  // sentence built from those would put our filler at the top of a claim
  // the applicant never made: an answer that only drops one card read back
  // as "your search is about compensation, balance and advancement".
  const said = settlePriorities(value)
    .ranked.slice(0, TOP_COUNT)
    .map(priorityPhrase)
  if (said.length === 0)
    return 'So far you have only said what does not matter. The list above is still our order — move what does matter to the top.'
  const last = said[said.length - 1]
  const text =
    said.length === 1 ? last : `${said.slice(0, -1).join(', ')} and ${last}`
  const order = said.length === 1 ? '' : ', in that order'
  return `Your search is about ${text}${order}.`
}

/**
 * What the drawn board would store if the applicant touched it, on top of
 * the answer it was drawn from.
 *
 * The board carries a third field — the top three — that is a drawing rather
 * than an answer, and every edit below applies to what is DRAWN rather than
 * to what is stored: an untouched answer stores nothing while the board shows
 * the starting cards, so editing the stored value directly would look for a
 * card that is not in it and refuse every first tap.
 *
 * `from` is what stops a ladder edit destroying the rest of the answer. The
 * board knows only the two piles, so everything else an answer holds —
 * today phase two, tomorrow whatever PR5 adds — has to be carried across,
 * and it is carried here rather than at each call site: a caller that forgot
 * would silently erase an answer the applicant had already given. Settled on
 * the way through, because `isPriorityValue` lets any shape ride under
 * `followUps` on purpose, and an unsettled one would reach the strict wire
 * schema as an error no ladder edit could clear.
 *
 * Named, and here, because deciding which of the board's fields are the
 * answer is this module's job. Spelled out field by field rather than
 * destructured off the board, so a field added to `PriorityBoard` later
 * cannot leak into a stored answer by accident.
 */
export function placedPriorities(
  board: PriorityBoard,
  from: PriorityValue,
): PriorityValue {
  return withFollowUps(
    { ranked: board.ranked, dropped: board.dropped },
    priorityFollowUps(from),
  )
}

/**
 * A control on a card, named for what it does rather than for where it is.
 * The module says which control the next render should focus; the component
 * is the only thing that knows what a ref is.
 */
export type PriorityControl = 'up' | 'down' | 'drop' | 'add'

/** Where focus belongs after an edit, in the widget's own vocabulary. */
export interface PriorityFocus {
  control: PriorityControl
  id: string
}

/** One tap on the board. */
export type PriorityTap =
  | { kind: 'move'; id: string; delta: -1 | 1 }
  | { kind: 'drop'; id: string }
  | { kind: 'restore'; id: string }

/**
 * The widget's whole interaction state, the same way `TimelineState` is the
 * timeline's. `value` is the answer; the other two are the memory of a
 * removal and where focus goes next, neither of which is ever stored.
 */
export interface PriorityState {
  value: PriorityValue
  /**
   * Where each card was when it was taken off the ladder, so adding it back
   * is an undo rather than a demotion to the bottom.
   *
   * Session-only on purpose. It exists for the mis-tap — the remove control
   * ends the row, and after a removal the next card's own remove control
   * lands under the same finger, so a stray second tap takes a card nobody
   * meant to take. That always happens seconds after the drop. A card
   * dropped before a reload is a card they meant to drop, and the bottom is
   * the right place for it.
   */
  droppedFrom: Record<string, number>
  /** Null except on the state that follows an edit that moved the DOM. */
  focus: PriorityFocus | null
}

/**
 * One tap on the board: the whole edit, and where focus goes afterwards.
 *
 * The two have to be one decision, which is why this is one function rather
 * than the three mutators above called separately. A refused edit — the top
 * card asked to go up — must save nothing AND move nothing, and it says so
 * by handing back the state it was given, by identity. Deciding focus apart
 * from the edit let a focus intent outlive an edit the parent never applied.
 *
 * `tapMonth` is the same shape on the timeline, and for the same reason: a
 * widget's rules, including which control survives its own edit, belong
 * beside the rules they depend on rather than in the drawing.
 */
export function tapPriority(
  state: PriorityState,
  tap: PriorityTap,
): PriorityState {
  // Drawn board in, stored answer out — `placedPriorities` is what carries
  // phase two across, so a tap on the ladder cannot erase it.
  const placed = placedPriorities(priorityBoard(state.value), state.value)
  const { droppedFrom } = state

  if (tap.kind === 'move') {
    const value = movePriority(placed, tap.id, tap.delta)
    if (value === placed) return state
    // Reordering is a real DOM move, and moving a node that contains
    // `document.activeElement` unfocuses it — asymmetrically, as it happens:
    // React's reconciler flags the card itself for placement moving down and
    // the card beside it moving up, so without this "move up" could be
    // chained and "move down" dropped a keyboard applicant at the top of the
    // form every single time.
    const control: PriorityControl = tap.delta === -1 ? 'up' : 'down'
    return { value, droppedFrom, focus: { control, id: tap.id } }
  }

  if (tap.kind === 'drop') {
    const value = dropPriority(placed, tap.id)
    if (value === placed) return state
    const at = placed.ranked.indexOf(tap.id)
    // Asked of the ladder this edit actually produced, never re-derived from
    // where the card used to sit: whoever now stands at that rank is the card
    // that slid into its place, and one rank up is the neighbour when it was
    // last. Change what `dropPriority` does with a removed card and this
    // still points at a control that exists.
    const heir = value.ranked[at] ?? value.ranked[at - 1]
    // Onto that card's `Move up`, never its `Take off the list`. Holding
    // Enter on a button repeats the click, and a phone tap lands twice on a
    // row that has just shifted up under the thumb — so handing the
    // survivor's remove control the focus arms the next repeat with the one
    // action this widget calls destructive and badly undoable. `Move up`
    // survives exactly as reliably, announces the same name and position,
    // and is the likelier next intent anyway. With nothing left on the
    // ladder, the pile chip the card just became is the only control there
    // is.
    const focus: PriorityFocus = heir
      ? { control: 'up', id: heir }
      : { control: 'add', id: tap.id }
    return { value, droppedFrom: { ...droppedFrom, [tap.id]: at }, focus }
  }

  const value = restorePriority(placed, tap.id, droppedFrom[tap.id])
  if (value === placed) return state
  // Focus follows the card onto `Move up` — the safe landing, and the move an
  // applicant who just added a card back almost always wants next.
  return { value, droppedFrom, focus: { control: 'up', id: tap.id } }
}

/** A priority phase two asks about, and where it sits on the ladder. */
export interface AskedPriority {
  id: string
  /**
   * Its place on the ladder, counting from one — NOT its place among the
   * priorities that survive the filter below.
   */
  rank: number
}

/**
 * Which of the drawn top three phase two has anything to ask about.
 *
 * Not simply the top three: a priority we have written no follow-up for would
 * otherwise draw a bordered card with a heading and nothing in it.
 *
 * Asked of `asksFollowUps` rather than of the pile table alone, because a
 * priority's follow-up is not always a pile — compensation's is a range, and
 * counting piles would have skipped it while the ladder said it mattered
 * most.
 *
 * Gate-independent on purpose: a card must not appear and vanish as the
 * applicant taps a chip inside another one.
 *
 * Read off the DRAWN board, so our own starting order is asked about too.
 */
export function askedPriorities(value: PriorityValue): AskedPriority[] {
  // Numbered BEFORE the filter, which is the whole reason the rank is carried
  // out rather than recovered by the caller: the position is exactly what
  // dropping a card destroys. Numbering what comes back instead prints the
  // default board's one real question as first when it ranked third.
  return priorityBoard(value)
    .top.map((id, index) => ({ id, rank: index + 1 }))
    .filter(({ id }) => asksFollowUps(id))
}

/**
 * The priorities phase two is about, named in one sentence.
 *
 * Shown at the top of phase two rather than only implied by the cards: the
 * ranking is what chose these questions, and somebody who does not know that
 * cannot tell why the flexibility pile vanished when they demoted a card.
 */
export function detailHint(value: PriorityValue): string {
  const asked = askedPriorities(value)
  if (asked.length === 0) return PRIORITY_COPY.detailEmpty
  return `These follow from your ranking: ${asked.map(({ id }) => priorityShortPhrase(id)).join(', ')}. Change the ranking and these change with it.`
}

/**
 * A priority as a short phrase mid-sentence: what comes before the first
 * slash or comma, lowercased.
 *
 * Both cuts, not just the slash. Three of the eight labels name one thing
 * twice — "Work-life balance / great company culture" — and one does it with
 * a comma instead: "Work flexibility, remote or hybrid" put whole into a
 * comma-separated list of three makes the sentence read as a list of four.
 */
function priorityShortPhrase(id: string): string {
  const [head = ''] = priorityLabel(id).split(/ \/ |, /)
  return head.charAt(0).toLowerCase() + head.slice(1)
}

/**
 * Phase two's answers off a stored value, settled and ready to draw.
 *
 * Every read goes through here rather than touching `value.followUps`, so a
 * resumed draft holding junk under that key opens as nothing said instead of
 * reaching the drawing half as a shape it cannot render.
 */
export function priorityFollowUps(value: PriorityValue): FollowUpAnswers {
  return isFollowUpAnswers(value.followUps)
    ? settleFollowUps(value.followUps)
    : emptyFollowUps()
}

/**
 * The same value with phase two's answers replaced.
 *
 * The piles are named field by field for the reason `placedPriorities` names
 * them: an answer is exactly these three keys, and a field added to
 * `PriorityValue` later must be carried across on purpose rather than by a
 * spread nobody revisits.
 */
export function withFollowUps(
  value: PriorityValue,
  followUps: FollowUpAnswers,
): PriorityValue {
  const next: PriorityValue = { ranked: value.ranked, dropped: value.dropped }
  if (hasFollowUpAnswers(followUps)) next.followUps = followUps
  return next
}

/**
 * A stored ranking, in today's vocabulary.
 *
 * `knownPriorities` runs on the way in, so the server enforces the same
 * fence the widget draws: only ids we offer, each in one pile, no duplicates.
 * Without it `saveStep` accepts an unbounded array of arbitrary strings,
 * `priorityLabel` passes an unknown id straight through, and a crafted
 * payload renders verbatim in the admin recap. The timeline beside it already
 * shares `timelineOrderProblem` with the server for exactly this reason.
 *
 * Idempotent, and it never invents a placement, so a real answer round-trips
 * unchanged. This is the strict half of the pair on purpose: the tolerant
 * `settlePriorities` is what the admin recap reads with, so retiring a
 * priority stops it being offered without deleting it from answers already
 * given.
 */
export const priorityValueSchema = z
  .object({
    ranked: z.array(z.string()),
    dropped: z.array(z.string()),
    // Optional, because every row written before phase two existed has no
    // such key and is still a valid ranking. The bags are typed loosely here
    // and narrowed by `knownFollowUps` in the transform, which is the one
    // place that knows which groups and options we actually offer.
    followUps: z
      .object({
        picks: z.record(z.string(), z.array(z.string())),
        // Bounded and trimmed the same way every other free-text answer is
        // (`textSchema` in `answer-shape.ts`). `knownFollowUps` narrows WHICH
        // keys survive and never caps the value, so without this the one
        // applicant-written sentence in this widget is the only prose in the
        // wizard that reaches the jsonb bag unbounded. Trimming belongs here
        // rather than in `settleFollowUps`, which runs per keystroke.
        typed: z.record(z.string(), z.string().trim().max(5000)),
        // Optional for the same reason `followUps` itself is: every row
        // written before the range shipped has two bags and is still a valid
        // answer. Bounds are NOT enforced here — `settleCompRange` in the
        // transform is the one place that knows the track's two ends and the
        // grid its thumbs land on, and a second copy of $1,000,000 in a
        // schema is a second copy to forget to change.
        ranges: z
          .record(
            z.string(),
            z.object({ min: z.number(), max: z.number() }).strict(),
          )
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .transform(knownPriorities)

/** Everything the rest of the wizard needs to know about this widget. */
export const prioritiesWidget: WidgetDescriptor = {
  // Empty, not the board we hand over: an untouched question must not leave
  // our own default ranking sitting in the answers bag. `priorityBoard` is
  // what draws the starting cards from this.
  empty: () => ({ ranked: [], dropped: [] }),
  is: isPriorityValue,
  // Settling drops a retired id, so a draft written before we changed the
  // list cannot submit a priority the schema no longer accepts. It takes
  // `unknown` and answers two empty piles for anything that is not a ranking,
  // so a draft holding a chip string this question never had still lands
  // somewhere the widget can draw.
  settle: (value) => knownPriorities(value),
  storedSchema: (field) =>
    field.required
      ? // Required means a ranking they GAVE, not merely an object that
        // arrived: two empty piles are what an untouched question sends, and
        // they parse fine while saying nothing at all.
        priorityValueSchema.refine(
          isPrioritiesAnswered,
          'Put what matters most at the top.',
        )
      : // An optional ranking normalizes a missing key to two empty piles,
        // the same way an optional timeline normalizes to an empty plan.
        // Deliberately NOT the starting board: writing our own default
        // ranking into every row would put words in the applicant's mouth in
        // the one place the ticket says must stay cheap to delete.
        priorityValueSchema
          .optional()
          .transform((v) => v ?? { ranked: [], dropped: [] }),
  // Settling already drops a retired id, and an unplaced card is drawn
  // somewhere reachable rather than held against Next.
  ready: () => true,
  summary: (value) => {
    // An unranked board summarises to '', which is how an untouched question
    // renders as nothing rather than as our own default order dressed up as
    // their answer.
    if (!isPriorityValue(value)) return []
    // Each half self-gates on `isPrioritiesAnswered`, so neither can leak our
    // default ranking. Built independently because `prioritiesSummary`
    // answers '' for BOTH "not answered" and "answered, but both piles are
    // empty" — and the second of those is the applicant who let our order
    // stand and answered only phase two. Conjoined, their follow-up lines
    // were thrown away and the recap printed a blank row.
    return [prioritiesSummary(value), ...followUpSummary(value)].filter(
      (line) => line !== '',
    )
  },
  list: () => false,
  prose: false,
}
